import React, { useState } from 'react'
import ReportLayout from '../../layouts/Report'

import useGetSchools from '../../../hooks/schools/useGetSchools'
import useGetCampuses from '../../../hooks/campuses/useGetCampuses'
import { useGetStudentOnboardingResponsesOptionsByField } from '../../../hooks/misc'
import useGetPageViewsReport from '../../../hooks/analytics/useGetPageViewsReport'
import useExportPageViewsReport from '../../../hooks/analytics/useExportPageViewsReport'

import ReportDatePicker from '../../sub_components/ReportDatePicker'
import FilterTable from '../../sub_components/FilterTable'

import { IColumnProps, IFilterableDataProps } from '../../../interfaces/table'
import { StudentOnboardingResponseOptionsField } from '../../../interfaces/types'
import BarChart from '../../sub_components/charts/BarChart'


const PageViewsReport = () => {
    const [schoolFilters, setSchoolFilters] = useState<number[]>([]);
    const [campusFilters, setCampusFilters] = useState<number[]>([]);
    const [programsOfStudyFilters, setProgramsOfStudyFilters] = useState<number[]>([]);
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");

    const { data: programsOfStudy } = useGetStudentOnboardingResponsesOptionsByField(StudentOnboardingResponseOptionsField.PROGRAM_OF_STUDY);

    const { mutate: exportCsv } = useExportPageViewsReport();

    const programsOfStudyOptions = programsOfStudy?.map((program, index) => ({
        id: index,
        name: program,
    })) ?? [];

    const filterQuery = new URLSearchParams();
    filterQuery.set("start_date", startDate);
    filterQuery.set("end_date", endDate);
    schoolFilters.forEach((id) => filterQuery.append("school_ids", id.toString()));
    campusFilters.forEach((id) => filterQuery.append("campus_ids", id.toString()));
    programsOfStudyFilters.forEach((id) => filterQuery.append("programs_of_study", programsOfStudyOptions[id].name));

    const { data: schools } = useGetSchools();
    const { data: campuses } = useGetCampuses();
    const { data: pageViewsReport } = useGetPageViewsReport(filterQuery.toString());

    let filterTableDataColumns__Schools: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "School Name", identifier: "name" },
    ];
    let filterTableDataColumns__Campuses: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Campus Name", identifier: "name" },
    ];
    let filterTableDataColumns__ProgramsOfStudy: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Program of Study", identifier: "name" },
    ];
    let filterTableDataColumns__PageViewsReport: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Full Name", identifier: "full_name" },
        { name: "Email", identifier: "email" },
        {
            name: "Group(s) with Views Count", identifier: "groups_data", render: (row: any, index: number) => {
                if (!row) return null;
                const rows = row.split(',');
                const groups = rows.map((row: any) => {
                    const [group, count] = row.split(' (');
                    return { group, count: count.replace(')', '') };
                });

                return <div className='immersion_admin__table__cell__content__flex_rows'>
                    {groups.map((group: any, index: number) => {
                        return <div key={index} className='immersion_admin__table__cell__content__flex_rows__item'>
                            <div className='immersion_admin__table__cell__content__flex_rows__item__text'>{group.group}</div>
                            <div className='immersion_admin__table__cell__content__flex_rows__item__text'>{group.count}</div>
                        </div>
                    })}
                </div>
            }
        },
    ];

    let filterTableData__Schools: IFilterableDataProps[] = [];
    let filterTableData__Campuses: IFilterableDataProps[] = [];
    let filterTableData__ProgramsOfStudy: IFilterableDataProps[] = [];
    let filterTableData__PageViewsReport: IFilterableDataProps[] = [];

    if (schools) {
        filterTableData__Schools = schools.map((school) => ({
            id: school.id,
            name: school.name,
        }));
    }

    if (campuses) {
        filterTableData__Campuses = campuses.map((campus) => ({
            id: campus.id,
            name: campus.name,
        }));
    }

    if (programsOfStudyOptions) {
        filterTableData__ProgramsOfStudy = programsOfStudyOptions.map((program) => ({
            id: program.id,
            name: program.name,
        }));
    }
    if (pageViewsReport) {
        filterTableData__PageViewsReport = pageViewsReport.table_data.map((user) => ({
            id: user.id,
            full_name: user.full_name,
            email: user.email,
            groups_data: user.groups_data.map((group) => group.group + ' (' + group.count + ')').join(', '),
        }));
    }

    const onExportCsvReport = () => {
        exportCsv({
            payload: {
                filterQuery: filterQuery.toString(),
            }
        });
    }

    return (
        <ReportLayout>
            <div className='immersion_admin__careers_report'>
                <div className='immersion_admin__careers_report__header'>
                    <div className='immersion_admin__careers_report__header__content'>
                        <h2 className='immersion_admin__careers_report__header__content__title'>Page Views Report</h2>
                        <p className='immersion_admin__careers_report__header__content__description'>
                            This slide gives an overview of page views to the platform since the start of data collection (20/04/24).
                            The chart to the bottom left shows a cumulative timeline of page views to the platform.
                            The table to the bottom right shows the latest date a user has had activity on the platform.
                        </p>
                    </div>
                    <div className='immersion_admin__careers_report__header__actions'>
                        <button className='immersion_admin__btn__primary immersion_admin__careers_report__header__actions__button' onClick={onExportCsvReport}>Export CSV</button>
                    </div>
                </div>

                <div className='immersion_admin__careers_report__chart_body'>
                    <div className='immersion_admin__careers_report__chart__row'>
                        <FilterTable
                            columns={filterTableDataColumns__Schools}
                            data={filterTableData__Schools}
                            enableFilters={true}
                            filters={schoolFilters}
                            setFilters={setSchoolFilters}
                        />
                        <FilterTable
                            columns={filterTableDataColumns__Campuses}
                            data={filterTableData__Campuses}
                            enableFilters={true}
                            filters={campusFilters}
                            setFilters={setCampusFilters}
                        />
                        <ReportDatePicker
                            required={true}
                            startDate={startDate}
                            setStartDate={setStartDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                        />
                    </div>
                    <div className='immersion_admin__careers_report__chart__row'>
                        <div className='immersion_admin__careers_report__chart__row immersion_admin__careers_report__chart__row__vertical'>
                            <FilterTable
                                columns={filterTableDataColumns__ProgramsOfStudy}
                                data={filterTableData__ProgramsOfStudy}
                                enableFilters={true}
                                filters={programsOfStudyFilters}
                                setFilters={setProgramsOfStudyFilters}
                            />
                            <FilterTable
                                columns={filterTableDataColumns__PageViewsReport}
                                data={filterTableData__PageViewsReport}
                                enableFilters={false}
                                filters={[]}
                                setFilters={() => { }}
                            />
                        </div>
                        {pageViewsReport?.page_views_chart_data.length ? (
                            <BarChart
                                width={400}
                                height={318}
                                data={pageViewsReport?.page_views_chart_data ?? []}
                                xLabel=""
                                yLabel="Number of Page Views"
                                xTickAngle={90}
                                xInterval={0}

                            />
                        ) : (
                            <div className='immersion_admin__careers_report__chart__no_data'>
                                No analytics data available
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </ReportLayout>
    )
}

export default PageViewsReport;